import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrganization } from '../../context/OrganizationContext';
import { auth } from '../../firebase';

const API_URL = process.env.REACT_APP_FIREBASE_FUNCTIONS_URL ||
  'https://us-central1-sistema-bks.cloudfunctions.net';

/**
 * FASE 3f — la lista de agendas de un bot.
 *
 * Sustituye al render binario «conectado / no conectado» que asumía UNA conexión
 * (`CalendarIntegration.jsx`, `isConnected = config?.calendar?.provider`). Ahora hay N
 * agendas, cada una con su estado.
 *
 * ⚠️ `por_confirmar` ES LO IMPORTANTE DE ESTA PANTALLA, no un adorno de estado.
 *
 * El link de invitación es un bearer token: quien lo tenga —y puede no ser quien debía—
 * conecta SU calendario. No hay identidad de PUX contra la que validarlo, porque el
 * profesional no es usuario de PUX. Lo único que separa «la Dra. Pérez conectó su agenda»
 * de «un desconocido conectó la suya» es que el dueño MIRE EL CORREO y lo confirme.
 *
 * DOS CAMINOS HACIA ESTA MISMA LISTA, y la diferencia importa:
 *
 *   «Conectar mi calendario» → OAuth directo, con sesión → queda `conectada`
 *   «Invitar a alguien»      → enlace, sin sesión        → queda `por_confirmar`
 *
 * No se pide confirmación en el primero porque no hay tercero a quien verificar: el dueño
 * está autenticado y ya sabemos quién es. Un paso de confirmación que no comprueba nada
 * enseñaría a pulsar «confirmar» sin mirar, que es justo lo que arruinaría la defensa en
 * el segundo.
 *
 * Por eso la tarjeta en `por_confirmar`:
 *   · enseña el correo real de la cuenta, grande y sin abreviar;
 *   · enseña al lado el correo que el dueño declaró, si lo declaró, para que la diferencia
 *     salte sin tener que recordarla;
 *   · dice explícitamente que el bot NO está usando esa agenda todavía;
 *   · pone «Rechazar» al mismo peso visual que «Confirmar». Un rechazo no es el camino
 *     excepcional: es la mitad de la razón por la que esta pantalla existe.
 *
 * Ver `functions/integrations/utils/agendaInvite.js` para el razonamiento completo.
 */

const ESTADOS = {
  pendiente: { clave: 'pendiente', color: '#8a8a8a' },
  por_confirmar: { clave: 'porConfirmar', color: '#c77700' },
  conectada: { clave: 'conectada', color: '#1a7f37' },
  error: { clave: 'error', color: '#c0392b' },
};

export default function ListaDeAgendas({ config, botId, onRecargar }) {
  const { t } = useTranslation('bots');
  // ⚠️ SE DESESTRUCTURA. `useOrganization()` devuelve el objeto entero del provider
  // (`{ user, context, organizations, switchContext, … }`), así que sin las llaves
  // `context.mode` y `context.orgId` son `undefined`, `organizationId` sale `null` SIEMPRE,
  // y el backend cae a la ruta personal — con HTTP 200 y sin un solo error.
  //
  // Pasó en el primer uso real (8-sep-2026): la agenda «Rodrigo» se creó en
  // `clientes/{uid}/bots/{botId}/…` en vez de `organizations/{orgId}/…`. Los otros doce
  // componentes del repo lo hacen bien; éste era el único que no. Lo caza ahora
  // `__tests__/useOrganizationDesestructurado.jest.test.js`.
  const { context } = useOrganization();

  const calendar = config?.calendar || {};
  const agendas = Array.isArray(calendar.agendas) ? calendar.agendas : [];
  const modo = calendar.modoSeleccion || 'preguntar';

  const [nombreNuevo, setNombreNuevo] = useState('');
  const [emailNuevo, setEmailNuevo] = useState('');
  const [ocupado, setOcupado] = useState(null);
  const [error, setError] = useState(null);
  const [linkVisible, setLinkVisible] = useState(null);

  const organizationId = context.mode === 'organization' && context.orgId ? context.orgId : null;

  /** Llamada autenticada a una de las funciones de agendas. */
  const llamar = async (fn, cuerpo) => {
    const user = auth.currentUser;
    if (!user) throw new Error(t('integrations.agendas.sesionExpirada'));
    const idToken = await user.getIdToken();
    const res = await fetch(`${API_URL}/${fn}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
      body: JSON.stringify({ organizationId, botId, ...cuerpo }),
    });
    const data = await res.json().catch(() => ({}));
    // El backend manda mensajes accionables (por qué no se puede, qué hacer): se muestran
    // tal cual en vez de aplanarlos a «error».
    if (!res.ok) throw new Error(data.error || t('integrations.agendas.errorGenerico'));
    return data;
  };

  const conBloqueo = (id, fn) => async () => {
    setError(null);
    setOcupado(id);
    try {
      await fn();
      // RECARGAR, no «actualizar». Este componente no escribe en Firestore desde el
      // cliente: todo pasa por los endpoints autenticados. Llamar aquí al `onUpdate` del
      // panel —que espera un OBJETO de cambios para escribirlos— reventaba con un
      // TypeError al desestructurar `undefined`, y el usuario veía «Error al actualizar»
      // después de una llamada que había devuelto 200.
      if (onRecargar) await onRecargar();
    } catch (e) {
      setError(e.message);
    } finally {
      setOcupado(null);
    }
  };

  const crear = conBloqueo('crear', async () => {
    const nombre = nombreNuevo.trim();
    if (!nombre) throw new Error(t('integrations.agendas.faltaNombre'));
    await llamar('crearAgenda', { nombre, emailDeclarado: emailNuevo.trim() || null });
    setNombreNuevo('');
    setEmailNuevo('');
  });

  /**
   * Camino 1: el dueño conecta SU cuenta sobre esta agenda. Redirige al proveedor y vuelve
   * a esta misma pantalla. Queda `conectada` directo — sin confirmación, porque no hay
   * tercero a quien verificar.
   */
  const conectarPropia = async (agendaId, provider) => {
    const data = await llamar('conectarAgendaPropia', {
      agendaId, provider, returnUrl: window.location.href,
    });
    if (!data.authUrl) throw new Error(t('integrations.agendas.errorGenerico'));
    window.location.href = data.authUrl;
  };

  const generarLink = (agendaId) => conBloqueo(agendaId, async () => {
    const data = await llamar('generarLinkAgenda', { agendaId });
    // El enlace se muestra UNA vez: el backend no lo guarda, sólo guarda su huella. Si el
    // dueño lo pierde, genera otro — y ese otro invalida este.
    setLinkVisible({ agendaId, url: data.url, vigenciaHoras: data.vigenciaHoras });
  })();

  return (
    <div className="lista-agendas">
      <div className="lista-agendas__cabecera">
        <h4>{t('integrations.agendas.titulo')}</h4>
        <p className="help-text">{t('integrations.agendas.ayuda')}</p>
      </div>

      {error && <div className="alerta alerta--error" role="alert">{error}</div>}

      {agendas.length === 0 && (
        <p className="help-text">{t('integrations.agendas.vacio')}</p>
      )}

      <ul className="lista-agendas__items">
        {agendas.map((a) => {
          const meta = ESTADOS[a.estado] || ESTADOS.pendiente;
          const esperando = a.estado === 'por_confirmar';
          return (
            <li key={a.id} className={`agenda-item agenda-item--${a.estado}`}>
              <div className="agenda-item__fila">
                <span className="agenda-item__nombre">{a.nombre || t('integrations.agendas.sinNombre')}</span>
                <span className="agenda-item__estado" style={{ color: meta.color }}>
                  {t(`integrations.agendas.estado.${meta.clave}`)}
                </span>
              </div>

              {a.estado === 'conectada' && a.emailConectado && (
                <div className="agenda-item__cuenta">{a.emailConectado}</div>
              )}

              {esperando && (
                <div className="agenda-item__confirmar">
                  {/* La verdad primero y sin abreviar: es lo único que el dueño tiene para
                      decidir, y truncarlo con puntos suspensivos escondería justo la parte
                      donde se nota que la cuenta no es la que esperaba. */}
                  <p className="agenda-item__cuenta agenda-item__cuenta--destacada">
                    {a.emailConectado || t('integrations.agendas.cuentaDesconocida')}
                  </p>
                  {a.emailDeclarado && a.emailDeclarado !== a.emailConectado && (
                    <p className="help-text">
                      {t('integrations.agendas.esperabas', { email: a.emailDeclarado })}
                    </p>
                  )}
                  <p className="agenda-item__aviso">{t('integrations.agendas.noSeUsaTodavia')}</p>
                  <div className="agenda-item__acciones">
                    <button
                      type="button"
                      disabled={ocupado === a.id}
                      onClick={conBloqueo(a.id, () => llamar('confirmarAgenda', { agendaId: a.id }))}
                    >
                      {t('integrations.agendas.confirmar')}
                    </button>
                    {/* Mismo peso visual que confirmar: rechazar no es el camino raro. */}
                    <button
                      type="button"
                      disabled={ocupado === a.id}
                      onClick={conBloqueo(a.id, () => llamar('rechazarAgenda', { agendaId: a.id }))}
                    >
                      {t('integrations.agendas.rechazar')}
                    </button>
                  </div>
                </div>
              )}

              <div className="agenda-item__acciones">
                {a.estado !== 'conectada' && (
                  <>
                    {/* Camino 1 — el dueño, para sí mismo. Primero porque es el caso
                        frecuente: la mayoría de los bots tienen un calendario, el suyo. */}
                    <button
                      type="button"
                      disabled={ocupado === a.id}
                      onClick={conBloqueo(a.id, () => conectarPropia(a.id, 'google'))}
                    >
                      {t('integrations.agendas.conectarGoogle')}
                    </button>
                    <button
                      type="button"
                      disabled={ocupado === a.id}
                      onClick={conBloqueo(a.id, () => conectarPropia(a.id, 'microsoft'))}
                    >
                      {t('integrations.agendas.conectarMicrosoft')}
                    </button>
                    {/* Camino 2 — otra persona. */}
                    <button type="button" disabled={ocupado === a.id} onClick={() => generarLink(a.id)}>
                      {a.invitacionHuella
                        ? t('integrations.agendas.reenviarLink')
                        : t('integrations.agendas.invitar')}
                    </button>
                  </>
                )}
                <button
                  type="button"
                  disabled={ocupado === a.id}
                  onClick={conBloqueo(a.id, async () => {
                    const nombre = window.prompt(t('integrations.agendas.nuevoNombre'), a.nombre || '');
                    if (nombre === null) return;
                    await llamar('renombrarAgenda', { agendaId: a.id, nombre });
                  })}
                >
                  {t('integrations.agendas.renombrar')}
                </button>
                <button
                  type="button"
                  disabled={ocupado === a.id}
                  onClick={conBloqueo(a.id, async () => {
                    if (!window.confirm(t('integrations.agendas.confirmarQuitar', { nombre: a.nombre || '' }))) return;
                    await llamar('quitarAgenda', { agendaId: a.id });
                  })}
                >
                  {t('integrations.agendas.quitar')}
                </button>
              </div>

              {linkVisible?.agendaId === a.id && (
                <div className="agenda-item__link">
                  {/* readOnly y seleccionable: el dueño lo copia y lo manda por donde
                      quiera. No se ofrece «enviar por correo» todavía porque eso añade un
                      canal más por el que el enlace puede acabar donde no debía. */}
                  <input type="text" readOnly value={linkVisible.url} onFocus={(e) => e.target.select()} />
                  <p className="help-text">
                    {t('integrations.agendas.vigencia', { horas: linkVisible.vigenciaHoras })}
                  </p>
                  <p className="help-text">{t('integrations.agendas.reenviarInvalida')}</p>
                  <p className="help-text">{t('integrations.agendas.linkQuedaPorConfirmar')}</p>
                </div>
              )}
            </li>
          );
        })}
      </ul>

      <div className="lista-agendas__nueva">
        <input
          type="text"
          value={nombreNuevo}
          placeholder={t('integrations.agendas.nombrePlaceholder')}
          onChange={(e) => setNombreNuevo(e.target.value)}
          maxLength={60}
        />
        {/* El correo es OPCIONAL y así se dice. Si se acierta, la agenda se activa sola;
            si no, va a confirmación — que es el camino normal, no un fallo. */}
        <input
          type="email"
          value={emailNuevo}
          placeholder={t('integrations.agendas.emailPlaceholder')}
          onChange={(e) => setEmailNuevo(e.target.value)}
        />
        <button type="button" disabled={ocupado === 'crear'} onClick={crear}>
          {t('integrations.agendas.agregar')}
        </button>
      </div>

      {/* El interruptor que quedó pendiente de 3d. Sólo tiene efecto con 2+ agendas
          conectadas: con una, los dos modos hacen exactamente lo mismo. */}
      <div className="lista-agendas__modo">
        <label htmlFor="modo-seleccion">{t('integrations.agendas.modo.titulo')}</label>
        <select
          id="modo-seleccion"
          value={modo}
          disabled={ocupado === 'modo'}
          onChange={(e) => conBloqueo('modo', () => llamar('guardarModoSeleccion', { modo: e.target.value }))()}
        >
          <option value="preguntar">{t('integrations.agendas.modo.preguntar')}</option>
          <option value="disponibilidad">{t('integrations.agendas.modo.disponibilidad')}</option>
        </select>
        <p className="help-text">{t(`integrations.agendas.modo.ayuda.${modo}`)}</p>
      </div>
    </div>
  );
}
